import React, { useEffect, useState } from "react";
import { useCustomer } from "@/context/CustomerContext";
import { freeOver } from "@/lib/storenav";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Tag, ShoppingBag, Check, ChevronRight, FileText, Upload, X } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { useCart } from "@/context/CartContext";
import StoreShell from "@/components/StoreShell";
import { Splash, ErrorView } from "./Storefront";
import { inr } from "@/lib/storeutils";
import { INDIA_STATES, validators, matchState } from "@/lib/india";
import api, { apiErr } from "@/lib/api";
import { openCheckout } from "@/lib/payment";
import { cartItemTax, sumTax } from "@/lib/tax";

export default function Checkout() {
  const s = useStore();
  const cart = useCart();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", mobile: "", address: "", city: "", state: "", pincode: "", country: "India" });
  const [errors, setErrors] = useState({});
  const [pinNote, setPinNote] = useState("");            // status text under the pincode field
  const [coupon, setCoupon] = useState("");
  const [applied, setApplied] = useState(null);
  const fulfillment = "delivery";                       // home delivery only
  const [picked, setPicked] = useState(null);          // the shopper's own choice of payment method; null = use the default
  const [quote, setQuote] = useState({ ok: false, loading: false, error: "" });
  const onlineOk = !!s.store?.online_payments;          // the vendor's own Razorpay keys are saved and switched on
  const [placing, setPlacing] = useState(false);
  const [success, setSuccess] = useState(null);
  const [rx, setRx] = useState([]);
  const [rxUploading, setRxUploading] = useState(false);
  const cu = useCustomer();
  // a logged-in shopper's details and default address pre-fill an empty form
  useEffect(() => {
    if (!cu?.customer) return undefined;
    let live = true;
    const fill = (a = {}) => live && setForm((f) => (f.name || f.email || f.mobile || f.address ? f : { ...f, name: cu.customer.name, email: cu.customer.email, mobile: cu.customer.mobile, address: a.address || "", city: a.city || "", state: a.state || "", pincode: a.pincode || "" }));
    cu.api.get("/addresses").then(({ data }) => fill(data.addresses.find((x) => x.is_default) || data.addresses[0])).catch(() => fill());
    return () => { live = false; };
  }, [cu?.customer?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pay Online (when the store accepts it) is the first option and is preselected; otherwise Cash on Delivery.
  const codOk = onlineOk ? s.store?.cod_enabled !== false : true;   // the vendor can switch Cash on Delivery off (only while online is available)
  const payMethod = !codOk ? "online" : onlineOk ? (picked || "online") : "cod";
  const payNow = payMethod;
  const cartKey = cart.items.map((i) => `${i.product_id}:${i.variant_id || ""}:${i.quantity}`).join("|");
  const pin = (form.pincode || "").trim();
  // the cart changed after a coupon was applied (e.g. quantity lowered below its minimum order): check it again
  const appliedCode = applied?.code;
  useEffect(() => {
    if (!appliedCode) return;
    api.post(`/store/${slug}/coupon/validate`, { code: appliedCode, subtotal: cart.subtotal, email: form.email.trim(), mobile: form.mobile.trim() })
      .then(({ data }) => setApplied((a) => (a && a.code === data.code && a.discount !== data.discount ? data : a)))
      .catch((err) => { setApplied(null); toast.error(apiErr(err.response?.data?.detail)); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart.subtotal]);

  // Entering a valid pincode fills City and State (the shopper can still edit them afterwards).
  useEffect(() => {
    if (!/^[1-9]\d{5}$/.test(pin)) { setPinNote(""); return undefined; }
    let live = true;
    setPinNote("Finding your city and state…");
    api.get(`/pincode/${pin}`).then(({ data }) => {
      if (!live) return;
      const state = matchState(data.state);
      setForm((f) => (f.pincode.trim() === pin ? { ...f, city: data.city || f.city, state: state || f.state } : f));
      setErrors((e) => ({ ...e, city: data.city ? "" : e.city, state: state ? "" : e.state }));
      setPinNote(data.city ? `${data.city}, ${data.state}` : "");
    }).catch((err) => { if (live) setPinNote(err.response?.status === 404 ? "We couldn't find this pincode. Please check it, or fill in the city and state yourself." : ""); });
    return () => { live = false; };
  }, [pin]);
  useEffect(() => {
    if (!/^[1-9]\d{5}$/.test(pin) || cart.items.length === 0 || !s.slug) { setQuote({ ok: false, loading: false, error: "" }); return undefined; }
    let live = true;
    setQuote((q) => ({ ...q, loading: true, error: "" }));
    const h = setTimeout(async () => {
      try {
        const { data } = await api.post(`/store/${s.slug}/shipping-quote`, {
          items: cart.items.map((i) => ({ product_id: i.product_id, name: i.name, price: i.price, quantity: i.quantity, variant: i.variant, variant_id: i.variant_id || null })),
          pincode: pin, payment_method: payNow });
        if (live) setQuote({ ok: true, loading: false, error: "", shipping: data.shipping, courier: data.courier, etd: data.etd, source: data.source });
      } catch (err) { if (live) setQuote({ ok: false, loading: false, error: err.response?.status === 400 ? apiErr(err.response?.data?.detail) : "" }); }
    }, 450);
    return () => { live = false; clearTimeout(h); };
  }, [s.slug, pin, cartKey, payNow]); // eslint-disable-line react-hooks/exhaustive-deps

  if (s.loading) return <Splash />;
  if (s.error) return <ErrorView error={s.error} />;
  const { c, font, border, muted, cardShadow, slug } = s;

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: "" })); };

  const discount = applied?.discount || 0;
  const taxLines = cart.items.map((i) => cartItemTax(i, s.products));          // the store's current rule (category tax from Setup, inclusive/exclusive from the product)
  const { added: tax, included: taxIncluded, label: taxLabel } = sumTax(taxLines);
  // Delivery charge: the cheapest courier rate for this cart and pincode (server-side, Shiprocket); until a valid pincode is
  // entered we show the standard estimate. The server recomputes the charge when the order is placed.
  const shipping = Math.ceil(quote.ok ? quote.shipping : (cart.subtotal === 0 || (freeOver(s.store) > 0 && cart.subtotal >= freeOver(s.store)) ? 0 : 49));
  const total = Math.max(0, cart.subtotal - discount + shipping + tax);

  const needsRx = !!s.store?.requires_prescription;
  const uploadRx = async (files) => {
    setRxUploading(true);
    try {
      for (const file of Array.from(files)) {
        if (rx.length >= 5) { toast.error("You can upload up to 5 files."); break; }
        const body = new FormData(); body.append("file", file);
        const { data } = await api.post(`/store/${slug}/prescription`, body);
        setRx((list) => [...list, data]);
      }
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setRxUploading(false); }
  };

  const validate = () => {
    const e = {
      name: validators.name(form.name),
      email: validators.email(form.email),
      mobile: validators.mobile(form.mobile),
      address: validators.required(form.address, "Address"),
      city: validators.required(form.city, "City"),
      state: validators.required(form.state, "State"),
      pincode: validators.pincode(form.pincode),
    };
    setErrors(e);
    if (needsRx && rx.length === 0) e.rx = "Upload a valid doctor's prescription to continue.";
    setErrors(e);
    return Object.values(e).every((x) => !x);
  };

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    try {
      const { data } = await api.post(`/store/${slug}/coupon/validate`, { code: coupon, subtotal: cart.subtotal, email: form.email.trim(), mobile: form.mobile.trim() });
      setApplied(data); toast.success(`Coupon ${data.code} applied — saved ${inr(data.discount)}`);
    } catch (err) { setApplied(null); toast.error(apiErr(err.response?.data?.detail)); }
  };

  const place = async () => {
    if (cart.items.length === 0) return toast.error("Your cart is empty.");
    if (!validate()) return toast.error("Please fix the highlighted fields.");
    setPlacing(true);
    try {
      const { data } = await api.post(`/store/${slug}/checkout`, {
        items: cart.items.map((i) => ({ product_id: i.product_id, name: i.name, price: i.price, quantity: i.quantity, variant: i.variant, variant_id: i.variant_id || null, sku: i.sku || null, image: i.image })),
        customer: form, coupon_code: applied?.code || null,
        fulfillment_type: fulfillment, payment_method: payNow, prescriptions: rx,
      });
      if (data.cod) { cart.clear(); setSuccess(data.order_number); setPlacing(false); return; }
      await openCheckout({
        payment: data.payment, name: s.store.name, description: `Order ${data.order_number}`,
        prefill: { name: form.name, email: form.email, contact: form.mobile }, themeColor: c.accent,
        onSuccess: async (res) => {
          try {
            const { data: v } = await api.post(`/store/${slug}/checkout/verify`, { order_id: data.order_id, ...res });
            cart.clear(); setSuccess(v.order_number);
          } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
          finally { setPlacing(false); }
        },
        onFail: () => { toast.error("Payment failed. Try again."); setPlacing(false); },
      });
    } catch (err) {
      const msg = apiErr(err.response?.data?.detail);
      if (applied && /coupon/i.test(msg)) setApplied(null);   // the coupon no longer qualifies: take it off so the total is honest
      toast.error(msg); setPlacing(false);
    }
  };

  if (success) {
    return (
      <StoreShell>
        <div className="max-w-lg mx-auto px-5 py-20 text-center" data-testid="checkout-success">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto" style={{ background: c.accent, color: "#fff" }}><Check size={30} /></div>
          <h1 className="text-2xl font-extrabold mt-5" style={{ fontFamily: font }}>Order placed successfully!</h1>
          <p className="mt-2" style={{ color: muted }}>Your order <b style={{ color: c.text }}>{success}</b> has been confirmed.{payMethod === "cod" ? " Pay on delivery." : ""}</p>
          <div className="flex gap-3 justify-center mt-7">
            <Link to={`/store/${slug}/track`} className="px-6 py-3 rounded-full font-bold" style={{ background: c.primary, color: "#fff" }}>Track Order</Link>
            <Link to={`/store/${slug}`} className="px-6 py-3 rounded-full font-bold border" style={{ borderColor: border, color: c.text }}>Continue Shopping</Link>
          </div>
        </div>
      </StoreShell>
    );
  }

  if (cart.items.length === 0) {
    return (
      <StoreShell>
        <div className="max-w-lg mx-auto px-5 py-20 text-center">
          <ShoppingBag size={44} className="mx-auto" style={{ color: muted }} />
          <h1 className="text-2xl font-extrabold mt-4" style={{ fontFamily: font }}>Your cart is empty</h1>
          <p className="mt-2" style={{ color: muted }}>Add some products before checking out.</p>
          <Link to={`/store/${slug}`} className="inline-block mt-6 px-6 py-3 rounded-full font-bold" style={{ background: c.accent, color: "#fff" }}>Browse products</Link>
        </div>
      </StoreShell>
    );
  }

  return (
    <StoreShell>
      <div className="max-w-6xl mx-auto px-5 py-5">
        <h1 className="text-2xl font-extrabold mb-3" style={{ fontFamily: font }}>Checkout</h1>
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Form */}
          <div className="lg:col-span-2 space-y-4" data-testid="checkout-form">
            <Section title="Contact & Delivery" c={c} font={font}>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-2.5">
                <Field label="Full Name" required placeholder="e.g. Rahul Sharma" value={form.name} onChange={(v) => set("name", v)} error={errors.name} c={c} border={border} muted={muted} testid="checkout-name" />
                <Field label="Email Address" required type="email" placeholder="you@example.com" value={form.email} onChange={(v) => set("email", v)} error={errors.email} c={c} border={border} muted={muted} testid="checkout-email" />
                <Field label="Mobile Number" required placeholder="10-digit mobile" value={form.mobile} onChange={(v) => set("mobile", v.replace(/\D/g, "").slice(0, 10))} error={errors.mobile} c={c} border={border} muted={muted} testid="checkout-mobile" prefix="+91" />
                <Field label="Pincode" required placeholder="6-digit pincode" value={form.pincode} onChange={(v) => set("pincode", v.replace(/\D/g, "").slice(0, 6))} error={errors.pincode} note={pinNote} c={c} border={border} muted={muted} testid="checkout-pincode" />
                <Field label="City" required placeholder="e.g. Mumbai" value={form.city} onChange={(v) => set("city", v)} error={errors.city} c={c} border={border} muted={muted} testid="checkout-city" />
                <div>
                  <Label text="State" required muted={muted} />
                  <select value={form.state} onChange={(e) => set("state", e.target.value)} data-testid="checkout-state"
                    className="w-full rounded-xl px-4 py-2 text-sm outline-none border bg-transparent transition"
                    style={{ borderColor: errors.state ? "#dc2626" : border, color: form.state ? c.text : muted }}>
                    <option value="" style={{ color: "#000" }}>Select state</option>
                    {INDIA_STATES.map((st) => <option key={st} value={st} style={{ color: "#000" }}>{st}</option>)}
                  </select>
                  {errors.state && <Err msg={errors.state} />}
                </div>
                <div className="sm:col-span-2">
                  <Label text="Address" required muted={muted} />
                  <textarea rows={2} placeholder="House no, street, area, landmark" value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="checkout-address"
                    className="w-full rounded-xl px-4 py-2 text-sm outline-none border bg-transparent transition resize-none"
                    style={{ borderColor: errors.address ? "#dc2626" : border, color: c.text }} />
                  {errors.address && <Err msg={errors.address} />}
                </div>
                <div>
                  <Label text="Country" muted={muted} />
                  <input value={form.country} disabled data-testid="checkout-country"
                    className="w-full rounded-xl px-4 py-2 text-sm outline-none border bg-transparent cursor-not-allowed"
                    style={{ borderColor: border, color: muted }} />
                </div>
              </div>
            </Section>

            {needsRx && (
              <Section title="Doctor's Prescription" c={c} font={font}>
                <div className="rounded-xl border p-3" style={{ borderColor: errors.rx ? "#dc2626" : border }} data-testid="checkout-prescription">
                  <p className="text-xs" style={{ color: muted }}>This store sells medicines. Upload a clear photo or PDF of your doctor&apos;s prescription; the pharmacist verifies it before dispatch.</p>
                  <label className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold border cursor-pointer" style={{ borderColor: c.accent, color: c.accent }}>
                    <Upload size={15} /> {rxUploading ? "Uploading…" : "Upload prescription"}
                    <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" multiple className="hidden" data-testid="checkout-prescription-input" disabled={rxUploading}
                      onChange={(e) => { if (e.target.files?.length) uploadRx(e.target.files); e.target.value = ""; }} />
                  </label>
                  <span className="text-[11px] ml-3" style={{ color: muted }}>JPG, PNG, WebP or PDF · max 5 MB each</span>
                  {rx.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {rx.map((f) => (
                        <li key={f.file} className="flex items-center gap-2 text-sm"><FileText size={15} style={{ color: c.accent }} /><span className="truncate">{f.name}</span>
                          <button onClick={() => setRx((l) => l.filter((x) => x.file !== f.file))} aria-label="Remove" className="ml-auto" style={{ color: muted }}><X size={15} /></button></li>
                      ))}
                    </ul>
                  )}
                  {errors.rx && <Err msg={errors.rx} />}
                </div>
              </Section>
            )}

            <Section title="Payment Method" c={c} font={font}>
              <div className={`grid ${onlineOk && codOk ? "sm:grid-cols-2" : ""} gap-3`}>
                {[...(onlineOk ? [["online", "Pay Online", "Cards, UPI, netbanking (Razorpay)"]] : []), ...(codOk ? [["cod", "Cash on Delivery", "Pay when your order arrives"]] : [])].map(([v, l, d]) => (
                  <button key={v} onClick={() => setPicked(v)} data-testid={`checkout-pay-method-${v}`} className="px-4 py-2.5 rounded-xl border text-left transition-all"
                    style={payMethod === v ? { borderColor: c.accent, background: `${c.accent}14` } : { borderColor: border }}>
                    <div className="flex items-center gap-2">
                      <span className="w-4 h-4 rounded-full border flex items-center justify-center" style={{ borderColor: payMethod === v ? c.accent : border }}>{payMethod === v && <span className="w-2 h-2 rounded-full" style={{ background: c.accent }} />}</span>
                      <span className="font-semibold text-sm">{l}</span>
                    </div>
                    <div className="text-xs mt-1 ml-6" style={{ color: muted }}>{d}</div>
                  </button>
                ))}
              </div>
            </Section>
          </div>

          {/* Summary */}
          <div>
            <div className="rounded-2xl p-6 sticky top-24" style={{ background: c.surface, border: `1px solid ${border}`, boxShadow: cardShadow }}>
              <h2 className="font-extrabold text-lg mb-4" style={{ fontFamily: font }}>Order Summary</h2>
              <div className="space-y-3 max-h-56 overflow-y-auto scrollbar-thin pr-1">
                {cart.items.map((it, idx) => { const t = taxLines[idx]; return (
                  <div key={it.lineId} className="flex gap-3 items-center" data-testid={`checkout-item-${it.product_id}`}>
                    <div className="w-12 h-12 rounded-lg overflow-hidden shrink-0" style={{ background: "#f1f5f9" }}>{it.image ? <img src={it.image} alt={it.name} className="w-full h-full object-cover" /> : null}</div>
                    <div className="flex-1 min-w-0"><div className="text-sm font-medium line-clamp-1">{it.name}</div><div className="text-xs" style={{ color: muted }}>Qty {it.quantity}{it.variant ? ` · ${it.variant}` : ""}</div></div>
                    <div className="text-right text-[11px] leading-tight shrink-0" style={{ color: muted }} data-testid="checkout-item-tax">
                      <div>Price {inr(t.line)}</div>
                      <div>Tax{t.rate ? ` ${t.rate}%` : ""}{t.inclusive && t.tax ? " (incl.)" : ""} {t.tax ? inr(t.tax) : "—"}</div>
                      <div className="text-sm font-semibold" style={{ color: c.text }}>{inr(t.total)}</div>
                    </div>
                  </div>
                ); })}
              </div>

              <div className="flex gap-2 mt-4">
                <div className="flex-1 flex items-center gap-2 rounded-xl px-3 border bg-transparent" style={{ borderColor: border }}>
                  <Tag size={15} style={{ color: muted }} />
                  <input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="Coupon code" data-testid="checkout-coupon-input" className="bg-transparent outline-none py-2.5 text-sm w-full" style={{ color: c.text }} />
                </div>
                <button onClick={applyCoupon} data-testid="checkout-apply-coupon" className="px-4 rounded-xl font-semibold text-sm" style={{ background: c.primary, color: "#fff" }}>Apply</button>
              </div>

              <div className="mt-4 pt-4 border-t space-y-2 text-sm" style={{ borderColor: border }}>
                <Row l="Subtotal" v={inr(cart.subtotal)} muted={muted} />
                {discount > 0 && <Row l={`Discount (${applied.code})`} v={"-" + inr(discount)} muted={muted} />}
                {tax > 0 && <Row l={`Tax (${taxLabel})`} v={"+ " + inr(tax)} muted={muted} />}
                {taxIncluded > 0 && <div className="text-[11px] -mt-1" style={{ color: muted }} data-testid="checkout-tax-included">Prices include {taxLabel} of {inr(taxIncluded)}</div>}
                <Row l={quote.loading ? "Shipping (calculating…)" : "Shipping"} v={shipping === 0 ? "Free" : inr(shipping)} muted={muted} />
                {quote.ok && quote.courier && <div className="text-[11px] -mt-1" style={{ color: muted }} data-testid="checkout-shipping-note">Delivery by {quote.courier}{quote.etd ? ` · by ${quote.etd}` : ""}</div>}
                {quote.error && <div role="alert" className="text-xs font-semibold text-red-600" data-testid="checkout-shipping-error">{quote.error}</div>}
                <div className="flex justify-between font-bold text-base pt-2 border-t" style={{ borderColor: border }}><span>Total</span><span style={{ color: c.primary }}>{inr(total)}</span></div>
              </div>

              <button onClick={place} disabled={placing || quote.loading || !!quote.error} data-testid="checkout-pay-btn" className="w-full py-3.5 rounded-full font-bold mt-5 hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2" style={{ background: c.accent, color: "#fff" }}>
                {placing ? "Processing…" : payMethod === "cod" ? `Place Order · ${inr(total)}` : `Pay ${inr(total)}`} {!placing && <ChevronRight size={18} />}
              </button>
              <p className="text-[11px] text-center mt-3" style={{ color: muted }}>By placing this order you agree to our Terms & Privacy Policy.</p>
            </div>
          </div>
        </div>
      </div>
    </StoreShell>
  );
}

function Section({ title, c, font, children }) {
  return (
    <div>
      <h2 className="font-extrabold text-base mb-2" style={{ fontFamily: font }}>{title}</h2>
      {children}
    </div>
  );
}
function Label({ text, required, muted }) {
  return <span className="block text-xs font-semibold uppercase tracking-wide mb-1.5" style={{ color: muted }}>{text}{required && <span style={{ color: "#dc2626" }}> *</span>}</span>;
}
function Err({ msg }) { return <span className="block text-xs mt-1" style={{ color: "#dc2626" }}>{msg}</span>; }
function Row({ l, v, muted }) { return <div className="flex justify-between"><span style={{ color: muted }}>{l}</span><span>{v}</span></div>; }

function Field({ label, required, type = "text", placeholder, value, onChange, error, note, c, border, muted, testid, prefix }) {
  return (
    <div>
      <Label text={label} required={required} muted={muted} />
      <div className="flex items-center rounded-xl border bg-transparent transition overflow-hidden" style={{ borderColor: error ? "#dc2626" : border }}>
        {prefix && <span className="pl-3 text-sm" style={{ color: muted }}>{prefix}</span>}
        <input type={type} placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
          className="w-full px-4 py-2 text-sm outline-none bg-transparent" style={{ color: c.text }} />
      </div>
      {error ? <Err msg={error} /> : note ? <span className="block text-[11px] mt-1" style={{ color: muted }} data-testid="checkout-pincode-note">{note}</span> : null}
    </div>
  );
}
